
import React, { useEffect } from 'react';
import Label from '../../components/ui/label';
import Input from '../../components/ui/input';
import { BasilEyeClosedOutline, IconamoonEyeLight } from '../../assets/icon/iconify';
import { Spinner } from '../../assets/icon/svg';
import { useDispatch } from 'react-redux';
import VerifyOtp from '../../components/module/auth/verify-otp';
import { setLoginCredential } from '../../features/auth/auth-slice';
import PhilLifeLogo from '../../assets/images/PFI 120 Years Logo_PhilLife 120 Member.png';
import { getRememberedLogins } from '../../utils/remembered-login.utils';
import { useAuthActions } from '../../hooks/actions/use-auth-actions';
import { useAuthDataState } from '../../hooks/data-state/use-auth-data-state';

export default function LoginPage() {
    const dispatch = useDispatch();

    const {
        isLoadingLogin,
        setRememberedLogins,
        rememberedLogins,
        rememberMe,
        setRememberMe,
        showPassword,
        setShowPassword,
        isBtnDisable,
        isVerify,
        handleLogin,
        handleSelectRememberedAccount,
        handleRemoveRememberedAccount,
    } = useAuthActions();

    const { loginCredentials } = useAuthDataState();

    useEffect(() => {
        const accounts = getRememberedLogins();
        setRememberedLogins(accounts);
    }, [setRememberedLogins]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        dispatch(setLoginCredential({ ...loginCredentials, [name]: value }));
    };

    return (
        <>
            <div className="relative h-[100dvh] w-full overflow-hidden bg-gradient-to-br from-[#f5f9fd] via-[#edf5fc] to-[#e4f0fa]">
                <div className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-[#186cb2]/5 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-40 -right-32 h-[30rem] w-[30rem] rounded-full bg-[#186cb2]/5 blur-3xl" />
                <div className="pointer-events-none absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/40 blur-3xl" />

                <div className="relative flex min-h-screen w-full items-center justify-center px-4 py-4 sm:px-6 lg:px-8">
                    <div className="grid w-full max-w-5xl overflow-hidden rounded-2xl border border-white/70 bg-white shadow-[0_25px_70px_-20px_rgba(24,108,178,0.25)] lg:grid-cols-2">
                        {/* Branding panel */}
                        <div className="relative hidden overflow-hidden bg-[#186cb2] px-10 py-10 text-white lg:flex lg:flex-col lg:justify-center xl:px-12">
                            <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-white/5" />
                            <div className="pointer-events-none absolute -bottom-32 -left-20 h-72 w-72 rounded-full bg-white/5" />

                            <div className="relative">
                                <div className="flex w-full items-center justify-center rounded-xl bg-white p-4 shadow-lg">
                                    <img src={PhilLifeLogo} alt="PhilLife" className="w-40 lg:w-80" />
                                </div>

                                <p className="mt-6 text-sm leading-6 text-blue-100">
                                    Securely access the PhilLife{' '}
                                    <span className="font-semibold text-white">
                                        Group Insurance System
                                    </span>{' '}
                                    to create, manage, and monitor franchise applications,
                                    group insurance proposals, and policy-related
                                    information—all in one centralized platform.
                                </p>

                                <div className="mt-7 space-y-3">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/10">
                                            <div className="h-2 w-2 rounded-full bg-white" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium">Secure Login</p>
                                            <p className="text-xs text-blue-200">Protected account access</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/10">
                                            <div className="h-2 w-2 rounded-full bg-white" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium">OTP Verification</p>
                                            <p className="text-xs text-blue-200">Additional security protection</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/10">
                                            <div className="h-2 w-2 rounded-full bg-white" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium">Protected User Access</p>
                                            <p className="text-xs text-blue-200">Role-based system access</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Login panel */}
                        <div className="flex min-w-0 items-center justify-center bg-white px-5 py-6 sm:px-8 sm:py-8 lg:px-10 lg:py-8 xl:px-12">
                            <div className="w-full min-w-0 max-w-md">
                                <div className="mb-4 flex justify-center lg:hidden">
                                    <img
                                        src={PhilLifeLogo}
                                        alt="PhilLife"
                                        className="w-60 max-w-full"
                                    />
                                </div>

                                <div className="mb-5">
                                    <h2 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
                                        Welcome Back
                                    </h2>
                                    <p className="mt-1 text-xs text-gray-500 sm:text-sm">
                                        Sign in to continue to your account.
                                    </p>
                                </div>

                                {/* Remembered accounts */}
                                {rememberedLogins.length > 0 && (
                                    <div className="mb-5 min-w-0">
                                        <div className="mb-2.5 flex items-center justify-between gap-2">
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-gray-800">
                                                    Previously used
                                                </p>
                                                <p className="text-[10px] text-gray-400">
                                                    Select an account to continue
                                                </p>
                                            </div>
                                            <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
                                                {rememberedLogins.length}
                                            </span>
                                        </div>

                                        {/* Mobile and tablet: one horizontally scrollable avatar row */}
                                        <div className="flex flex-nowrap items-center gap-3 overflow-x-auto overflow-y-hidden px-1 pb-2 pt-1 lg:hidden [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-200">
                                            {rememberedLogins.map((account) => {
                                                const isSelected =
                                                    loginCredentials?.email?.toLowerCase() === account.email.toLowerCase();

                                                return (
                                                    <div key={account.email} className="group relative shrink-0">
                                                        <button
                                                            type="button"
                                                            disabled={isLoadingLogin}
                                                            onClick={() => handleSelectRememberedAccount(account)}
                                                            title={account.email}
                                                            aria-label={`Select account ${account.email}`}
                                                            className={`flex h-11 w-11 items-center justify-center rounded-full border-2 text-sm font-bold uppercase transition-all duration-200 ${
                                                                isSelected
                                                                    ? 'border-[#186cb2] bg-[#186cb2] text-white shadow-md shadow-[#186cb2]/20'
                                                                    : 'border-gray-200 bg-gray-100 text-gray-500 hover:border-[#186cb2] hover:bg-blue-50 hover:text-[#186cb2]'
                                                            }`}
                                                        >
                                                            {account.email.charAt(0)}
                                                        </button>

                                                        {isSelected && (
                                                            <span className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full border-2 border-white bg-[#186cb2]">
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                                    <path d="m5 12 4 4L19 6" />
                                                                </svg>
                                                            </span>
                                                        )}

                                                        <button
                                                            type="button"
                                                            disabled={isLoadingLogin}
                                                            onClick={(e) => handleRemoveRememberedAccount(e, account.email)}
                                                            aria-label={`Remove ${account.email}`}
                                                            title={`Remove ${account.email}`}
                                                            className="absolute -right-1 -top-1 z-10 flex h-5 w-5 items-center justify-center rounded-full border border-white bg-gray-200 text-xs leading-none text-gray-600 transition hover:bg-red-500 hover:text-white"
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Laptop and desktop: existing account cards */}
                                        <div className="hidden max-h-[132px] overflow-y-auto pr-1 lg:block [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-200 hover:[&::-webkit-scrollbar-thumb]:bg-gray-300">
                                            <div className="grid grid-cols-2 gap-2">
                                                {rememberedLogins.map((account) => {
                                                    const isSelected =
                                                        loginCredentials?.email?.toLowerCase() === account.email.toLowerCase();

                                                    return (
                                                        <div
                                                            key={account.email}
                                                            onClick={() => handleSelectRememberedAccount(account)}
                                                            className={`group relative cursor-pointer rounded-xl border p-2 transition-all duration-200 ${
                                                                isSelected
                                                                    ? 'border-[#186cb2] bg-[#186cb2]/5 shadow-sm'
                                                                    : 'border-gray-200 bg-gray-50 hover:border-[#186cb2]/50 hover:bg-blue-50'
                                                            }`}
                                                        >
                                                            <button
                                                                type="button"
                                                                disabled={isLoadingLogin}
                                                                onClick={(e) => handleRemoveRememberedAccount(e, account.email)}
                                                                className="absolute right-1 top-1 z-10 flex h-5 w-5 items-center justify-center rounded-full text-gray-300 opacity-0 transition hover:bg-red-50 hover:text-red-500 group-hover:opacity-100"
                                                                aria-label={`Remove ${account.email}`}
                                                            >
                                                                ×
                                                            </button>

                                                            <div className="flex min-w-0 items-center gap-2">
                                                                <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold uppercase text-white ${
                                                                    isSelected
                                                                        ? 'bg-[#186cb2]'
                                                                        : 'bg-gray-400 group-hover:bg-[#186cb2]'
                                                                }`}>
                                                                    {account.email.charAt(0)}
                                                                </div>

                                                                <div className="min-w-0 flex-1">
                                                                    <p title={account.email} className="truncate text-[10px] font-semibold text-gray-800">
                                                                        {account.email}
                                                                    </p>
                                                                    <p className={`mt-0.5 text-[8px] ${isSelected ? 'text-[#186cb2]' : 'text-gray-400'}`}>
                                                                        {isSelected ? 'Selected' : 'Remembered'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        handleLogin();
                                    }}
                                    className="space-y-4"
                                >
                                    <div>
                                        <Label
                                            label="Email"
                                            isRequired
                                            requiredText=" *"
                                            textColor="mb-1.5"
                                        />

                                        <div className="relative">
                                            <div className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-gray-400">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect width="20" height="16" x="2" y="4" rx="2" />
                                                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                                                </svg>
                                            </div>

                                            <Input
                                                name="email"
                                                type="email"
                                                disabled={isLoadingLogin}
                                                value={loginCredentials.email}
                                                onChange={handleChange}
                                                required
                                                placeholder="you@example.com"
                                                isCustomStyle
                                                customCss="block w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-3 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 hover:border-gray-300 focus:border-[#186cb2] focus:bg-white focus:ring-4 focus:ring-[#186cb2]/10"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <Label
                                            label="Password"
                                            isRequired
                                            requiredText=" *"
                                            textColor="mb-1.5"
                                        />

                                        <div className="relative">
                                            <div className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-gray-400">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect width="18" height="11" x="3" y="11" rx="2" />
                                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                                </svg>
                                            </div>

                                            <Input
                                                name="password"
                                                type={showPassword ? 'text' : 'password'}
                                                value={loginCredentials.password}
                                                onChange={handleChange}
                                                disabled={isLoadingLogin}
                                                required
                                                placeholder="Enter your password"
                                                isCustomStyle
                                                customCss="block w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-11 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 hover:border-gray-300 focus:border-[#186cb2] focus:bg-white focus:ring-4 focus:ring-[#186cb2]/10"
                                            />

                                            {loginCredentials.password.length > 0 && (
                                                <button
                                                    type="button"
                                                    disabled={isLoadingLogin}
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                    className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center justify-center rounded-lg p-1 text-gray-400 transition hover:bg-blue-50 hover:text-[#186cb2]"
                                                >
                                                    {showPassword ? (
                                                        <IconamoonEyeLight width={19} height={19} />
                                                    ) : (
                                                        <BasilEyeClosedOutline width={19} height={19} />
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center">
                                        <label className="group flex cursor-pointer select-none items-center gap-2.5">
                                            <div className="relative">
                                                <input
                                                    type="checkbox"
                                                    checked={rememberMe}
                                                    onChange={(e) => setRememberMe(e.target.checked)}
                                                    disabled={isLoadingLogin}
                                                    className="peer sr-only"
                                                />

                                                <div className="h-5 w-5 rounded-md border border-gray-300 bg-white transition-all peer-checked:border-[#186cb2] peer-checked:bg-[#186cb2] peer-focus-visible:ring-4 peer-focus-visible:ring-[#186cb2]/10 group-hover:border-[#186cb2]" />

                                                <svg className="pointer-events-none absolute left-0.5 top-0.5 hidden h-4 w-4 text-white peer-checked:block" viewBox="0 0 20 20" fill="currentColor">
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.25 7.3a1 1 0 0 1-1.42.006L3.29 9.34a1 1 0 1 1 1.42-1.407l4.039 4.077 6.543-6.585a1 1 0 0 1 1.412-.006Z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            </div>

                                            <span className="text-sm font-medium text-gray-600 group-hover:text-gray-800">
                                                Remember me
                                            </span>
                                        </label>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={isBtnDisable || isLoadingLogin}
                                        className={`group relative flex w-full items-center justify-center overflow-hidden rounded-xl py-2.5 text-sm font-semibold shadow-sm transition-all duration-200 ${
                                            isBtnDisable || isLoadingLogin
                                                ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                                                : 'bg-[#186cb2] text-white hover:bg-[#155f9d] hover:shadow-lg hover:shadow-[#186cb2]/20 active:scale-[0.99]'
                                        }`}
                                    >
                                        {isLoadingLogin ? (
                                            <div className="flex items-center justify-center gap-2">
                                                <Spinner size={18} />
                                                <span>Signing in...</span>
                                            </div>
                                        ) : (
                                            <span>Sign In</span>
                                        )}
                                    </button>
                                </form>

                                <div className="mt-4 flex items-center justify-center gap-2 text-[10px] text-gray-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <rect width="18" height="11" x="3" y="11" rx="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                    </svg>
                                    <span>Your connection is secure</span>
                                </div>

                                <p className="mt-3 text-center text-[10px] leading-4 text-gray-400">
                                    © {new Date().getFullYear()} PhilLife Financial.
                                    <br />
                                    All rights reserved.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {isVerify && <VerifyOtp />}
        </>
    );
}
